/**
 * Feb 2026 · Form Submissions — Merge queue.
 *
 * Lists submissions that matched an existing lead (form's
 * `duplicate_action` = queue_for_review). For each submission the user
 * picks per-field which value wins (existing vs incoming) or rejects the
 * merge entirely.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, Check, X, Loader2 } from "lucide-react";

export default function FormMergeQueue() {
  const { formId } = useParams();
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [picks, setPicks] = useState({});   // field -> "existing"|"incoming"

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/form-merge-queue`, { params: formId ? { form_id: formId } : {} });
      setRows(data?.submissions || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, formId]);

  const apply = async (sub) => {
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/form-merge-queue/${sub.id}/apply`, {
        picks, also_merge_extra_data: true,
      });
      toast.success("Lead updated");
      setOpenId(null); setPicks({});
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Merge failed")); }
  };
  const reject = async (sub) => {
    if (!window.confirm("Reject this submission? The existing lead won't be touched.")) return;
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/form-merge-queue/${sub.id}/reject`);
      toast.success("Rejected");
      setOpenId(null);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "Reject failed")); }
  };

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 md:p-8" data-testid="form-merge-queue-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => nav(formId ? `/app/forms/${formId}` : "/app/forms")}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold tracking-tight text-navy">Merge queue</h1>
        <span className="text-sm text-muted-foreground">{rows.length} pending</span>
      </div>

      {busy && !rows.length && <div className="text-center text-muted-foreground py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}
      {!busy && rows.length === 0 && (
        <div className="border border-border p-10 text-center text-muted-foreground text-sm">
          Nothing pending. When a form is set to “Hold in the merge queue” and a submission matches an existing lead, it lands here for review.
        </div>
      )}

      <div className="space-y-3">
        {rows.map((s) => {
          const isOpen = openId === s.id;
          const existing = s.matched_lead || {};
          const incoming = s.candidate_patch || {};
          const keys = Array.from(new Set([...Object.keys(existing), ...Object.keys(incoming)]))
            .filter((k) => !["id", "workspace_id", "created_at", "updated_at", "extra_data", "assigned_to", "deleted_at", "created_via", "created_from_form_id", "_id"].includes(k) && k in incoming);
          return (
            <div key={s.id} className="border border-border bg-background" data-testid={`merge-row-${s.id}`}>
              <button className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-muted/40"
                onClick={() => { setOpenId(isOpen ? null : s.id); setPicks({}); }}>
                <ChevronRight className={`w-4 h-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{incoming.name || incoming.email || "(no name)"}</div>
                  <div className="text-[11px] text-muted-foreground">
                    matches lead <code>{s.matched_lead_id?.slice(0, 8)}</code> · {String(s.created_at || "").slice(0, 16)} · {(s.utm?.utm_source || "direct")}
                  </div>
                </div>
              </button>
              {isOpen && (
                <div className="border-t border-border p-3 space-y-2 bg-muted/20">
                  <div className="grid grid-cols-[minmax(0,1fr),minmax(0,2fr),minmax(0,2fr),90px] text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    <div>Field</div><div>Existing lead</div><div>Incoming</div><div className="text-right">Winner</div>
                  </div>
                  {keys.map((k) => (
                    <div key={k} className="grid grid-cols-[minmax(0,1fr),minmax(0,2fr),minmax(0,2fr),90px] items-center py-1 border-t border-border text-xs">
                      <div className="font-mono">{k}</div>
                      <div className="truncate" title={String(existing[k] ?? "")}>{String(existing[k] ?? "—")}</div>
                      <div className="truncate font-semibold" title={String(incoming[k] ?? "")}>{String(incoming[k] ?? "—")}</div>
                      <div className="flex gap-1 justify-end">
                        <button
                          className={`text-[10px] px-2 py-0.5 border ${(picks[k] || "existing") === "existing" ? "bg-slate-200 border-slate-400" : "border-border hover:bg-muted"}`}
                          onClick={() => setPicks((p) => ({ ...p, [k]: "existing" }))}
                          data-testid={`merge-pick-${k}-existing`}>Keep</button>
                        <button
                          className={`text-[10px] px-2 py-0.5 border ${picks[k] === "incoming" ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
                          onClick={() => setPicks((p) => ({ ...p, [k]: "incoming" }))}
                          data-testid={`merge-pick-${k}-incoming`}>Use new</button>
                      </div>
                    </div>
                  ))}
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" className="rounded-none" onClick={() => reject(s)} data-testid={`merge-reject-${s.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                    <Button size="sm" className="rounded-none" onClick={() => apply(s)} data-testid={`merge-apply-${s.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Apply merge
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
